// @ts-nocheck
import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Dialog, DialogContent, DialogTitle,
  CircularProgress, List, ListItem, ListItemSecondaryAction,
  ListItemText, Radio, TextField, Typography
} from '@material-ui/core';
import QrReader from 'react-qr-reader';
import CloseIcon from '@material-ui/icons/Close';
import VideocamIcon from '@material-ui/icons/Videocam';
import CropFreeIcon from '@material-ui/icons/CropFree';
import Button from '@material-ui/core/Button';
import { useTranslation } from 'react-i18next';

import SuccessScreen from './SuccessScreen';
import { shouldUseCallOffInAutomationModals } from '../../../../utils/tenantFeatures';

const AutoScannModal = ({
  open,
  onClose,
  onScan,
  onSuccessRefresh,
  loadingExternal,
  stepData,
  objects,
}) => {
  const SCAN_DEBOUNCE_MS = 2000;
  const { t } = useTranslation();
  const useCallOffReference = shouldUseCallOffInAutomationModals();
  const [buffer, setBuffer] = useState("");
  const [subdivision, setSubdivision] = useState(1);
  const [cameraActive, setCameraActive] = useState(false);
  const [waitingDecision, setWaitingDecision] = useState(false);
  const [waitingConfirmation, setWaitingConfirmation] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [scanFeedback, setScanFeedback] = useState('');
  const [selectedBranchKey, setSelectedBranchKey] = useState('');
  const lastValidScanAtRef = useRef(0);
  const lastFeedbackAtRef = useRef(0);

  const showInvalidQrFeedback = () => {
    const now = Date.now();
    if (now - lastFeedbackAtRef.current < 1500) return;
    lastFeedbackAtRef.current = now;
    setScanFeedback(t('process.autoScan.invalidQrFormat'));
  };

  useEffect(() => {
    if (stepData?.status === 'WAITING_DECISION') {
      setWaitingDecision(true);
      setWaitingConfirmation(false);
      setSuccessData(null);
      setSelectedBranchKey(stepData?.selectedBranchKey || '');
    } else if (stepData?.status === 'WAITING_SUBDIVISION') {
      setWaitingDecision(false);
      setWaitingConfirmation(true);
      setSuccessData(null);
      setSelectedBranchKey(stepData?.selectedBranchKey || '');
    } else if (
      stepData?.status === 'transition_success'
      || stepData?.status === 'completed_route'
    ) {
      setSuccessData(stepData);
      setWaitingDecision(false);
      setWaitingConfirmation(false);
    }
  }, [stepData]);

  const handleCloseInternal = () => {
    setCameraActive(false);
    setWaitingDecision(false);
    setWaitingConfirmation(false);
    setSuccessData(null);
    setScanFeedback('');
    setSelectedBranchKey('');
    lastValidScanAtRef.current = 0;
    setBuffer("");
    setSubdivision(1);
    onClose();
  };

  const handleSuccessMainAction = (id) => {
    window.open(`/process/${id}`, '_blank');
    if (typeof onSuccessRefresh === 'function') onSuccessRefresh();
  };

  const handleSuccessSecondaryAction = () => {
    if (typeof onSuccessRefresh === 'function') onSuccessRefresh();
    handleCloseInternal();
  };

  const processCode = (rawCode) => {
    if (!rawCode || loadingExternal || successData) return;
    try {
      const code = rawCode.trim();

      if (!code.startsWith('PK,')) {
        showInvalidQrFeedback();
        return;
      }

      const parts = code.split(',');
      const lotPart = parts.find(p => p.startsWith('l:'))?.split(':')[1];
      const pakPart = parts.find(p => p.startsWith('p:'))?.split(':')[1];

      if (lotPart && pakPart) {
        const now = Date.now();
        if (now - lastValidScanAtRef.current < SCAN_DEBOUNCE_MS) {
          setScanFeedback(t('process.autoScan.scanDebounceActive'));
          return;
        }

        setScanFeedback('');
        lastValidScanAtRef.current = now;
        onScan({
          lotNumber: lotPart,
          pakId: pakPart
        });
      } else {
        showInvalidQrFeedback();
      }
    } catch (error) {
      showInvalidQrFeedback();
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!open || loadingExternal || waitingDecision || waitingConfirmation || successData) return;
      if (e.keyCode === 13) {
        if (buffer.length > 0) {
          processCode(buffer);
          setBuffer("");
        }
      } else if (e.key.length === 1) {
        setBuffer(prev => prev + e.key);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, buffer, loadingExternal, waitingDecision, waitingConfirmation, successData, t]);

  const handleScanResult = (data) => {
    if (data && !loadingExternal && !successData) {
      processCode(data);
      setCameraActive(false);
    }
  };

  const handleConfirmSubdivision = () => {
    if (!stepData?.lotNumber || !stepData?.pakId) {
      alert(t('process.autoScan.missingLotData'));
      return;
    }

    const parsedSubdivision = parseInt(subdivision, 10);
    const subdivisionCount = Number.isFinite(parsedSubdivision) && parsedSubdivision > 0
      ? parsedSubdivision
      : 1;

    try {
      onScan({
        lotNumber: stepData.lotNumber,
        pakId: stepData.pakId,
        subdivisionCount,
        selectedBranchKey: stepData?.selectedBranchKey || selectedBranchKey || undefined,
      });
    } catch (error) {
      alert(
        t('process.autoScan.startSubdivisionError', {
          detail: error?.message || 'sin detalle',
        }),
      );
    }
  };

  const handleConfirmDecision = () => {
    if (!stepData?.pakId) {
      alert(t('process.autoScan.missingLotData'));
      return;
    }

    if (!selectedBranchKey) {
      alert(t('Debe seleccionar una respuesta'));
      return;
    }

    onScan({
      lotNumber: stepData?.lotNumber,
      pakId: stepData.pakId,
      selectedBranchKey,
    });
  };

  return (
    <Dialog open={open} onClose={handleCloseInternal} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {successData
              ? ""
              : waitingDecision
                ? t('Selecciona una respuesta')
                : waitingConfirmation
                ? t('process.autoScan.title.configureOutput')
                : t('process.autoScan.title.automaticMode')}
          </Typography>
          <CloseIcon onClick={handleCloseInternal} style={{ cursor: 'pointer' }} />
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column" alignItems="center" pb={3} minHeight={300} justifyContent="center">

          {loadingExternal ? (
            <Box py={4} display="flex" flexDirection="column" alignItems="center">
              <CircularProgress color="secondary" size={60} />
              <Box mt={2}>
                <Typography variant="caption">{t('process.autoScan.processing')}</Typography>
              </Box>
            </Box>
          ) :

            successData ? (
              <SuccessScreen
                data={successData}
                objects={objects}
                onMainAction={handleSuccessMainAction}
                onSecondaryAction={handleSuccessSecondaryAction}
              />
            ) :

              waitingDecision ? (
                <Box width="100%">
                  <Typography variant="h6" gutterBottom color="primary">
                    {stepData?.processName || t('process.autoScan.stationFallback')}
                  </Typography>
                  <Typography variant="body1">
                    {stepData?.decisionPrompt || t('Selecciona la opción que corresponda')}
                  </Typography>
                  <List>
                    {(stepData?.decisionOptions || []).map((option) => (
                      <ListItem
                        button
                        key={option.key}
                        selected={selectedBranchKey === option.key}
                        onClick={() => setSelectedBranchKey(option.key)}
                        style={{ borderRadius: 8, margin: '12px 0', backgroundColor: selectedBranchKey === option.key ? '#e3f2fd' : undefined }}
                      >
                        <Radio
                          checked={selectedBranchKey === option.key}
                          color="primary"
                        />
                        <ListItemText primary={option.label} />
                      </ListItem>
                    ))}
                  </List>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={handleConfirmDecision}
                    disabled={loadingExternal || !selectedBranchKey}
                  >
                    {t('Confirmar decisión')}
                  </Button>
                </Box>
              ) : waitingConfirmation ? (
                <Box width="100%">
                  <Typography variant="h6" gutterBottom color="primary">
                    {t('process.autoScan.station', {
                      processName:
                        stepData?.processName || t('process.autoScan.stationFallback'),
                    })}
                  </Typography>
                  <Typography variant="body1">
                    {useCallOffReference
                      ? (
                        <>
                          {t('process.autoScan.detectedCallOff')} <b>{stepData?.callOff || 'SIN CALL OFF'}</b>
                        </>
                      )
                      : (
                        <>
                          {t('process.autoScan.detectedLot')} <b>{stepData?.lotNumber}</b>
                        </>
                      )}
                  </Typography>
                  <List>
                    {!!stepData?.selectedBranchLabel && (
                      <ListItem selected style={{ borderRadius: 8, margin: '16px 0 0', backgroundColor: '#e3f2fd' }}>
                        <ListItemText
                          primary={t('Respuesta seleccionada')}
                          secondary={stepData.selectedBranchLabel}
                        />
                      </ListItem>
                    )}
                    <ListItem selected style={{ borderRadius: 8, margin: '16px 0', backgroundColor: '#e8f5e9' }}>
                      <ListItemText
                        primary={t('process.autoScan.subdivisionQuestion')}
                        secondary={t('process.autoScan.subdivisionHelp')}
                      />
                      <ListItemSecondaryAction>
                        <TextField
                          type="number" size="small" variant="outlined" style={{ width: 80, backgroundColor: 'white' }}
                          value={subdivision}
                          onChange={(e) => setSubdivision(e.target.value)}
                          inputProps={{ min: 1, step: 1 }}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  </List>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={handleConfirmSubdivision}
                    disabled={loadingExternal || !stepData?.lotNumber || !stepData?.pakId}
                  >
                    {t('process.autoScan.processAndPrint')}
                  </Button>
                </Box>
              ) : (
                <>
                  <Box mb={2} width="100%" display="flex" justifyContent="center">
                    {cameraActive ? (
                      <Box width="100%" maxWidth={300} style={{ borderRadius: 12, overflow: 'hidden', border: '4px solid #18897B' }}>
                        <QrReader delay={300} onError={(err) => alert(err)} onScan={handleScanResult} style={{ width: '100%' }} facingMode="environment" />
                      </Box>
                    ) : (
                      <CropFreeIcon style={{ fontSize: 120, color: '#ccc' }} />
                    )}
                  </Box>
                  <Typography variant="h5">{t('process.autoScan.readyToScan')}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t('process.autoScan.scanHelp')}
                  </Typography>
                  {!!scanFeedback && (
                    <Box mt={1}>
                      <Typography variant="caption" style={{ color: '#c62828' }}>
                        {scanFeedback}
                      </Typography>
                    </Box>
                  )}
                  {!cameraActive && (
                    <Box mt={3}>
                      <Button variant="outlined" color="primary" startIcon={<VideocamIcon />} onClick={() => setCameraActive(true)}>
                        {t('process.autoScan.activateCamera')}
                      </Button>
                    </Box>
                  )}
                </>
              )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AutoScannModal;
